import {
  UserOutlined,
  LogoutOutlined,
  DownOutlined,
  PoweroffOutlined,
  MailOutlined,
  BellOutlined,
} from "@ant-design/icons";
import { Layout, Menu, Avatar, Dropdown, Typography, Select, Badge, Space, Empty } from "antd";
import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux"; // MODIFIED: Added useSelector
import Logo from "../../Images/logo.png";
import { getUserData, setLoginData } from "../login/LoginSlice";
import { getNotificationData, updateNotificationData, getCorporateFinancialYears } from "../../Action/Actions";
import dayjs from "dayjs";
import MenuItems from "./MenuItems";
import MenuHandler from "./MenuState";
import { useSelectedYear } from "../../Context/SelectYearContext";
import Translate from "./Translate";
import "./layout.css";
import MyAccount from "../MyAccount/MyAccount";

// ADDED: Import actions and selectors from the new slice
import {
  setFinancialYears,
  setSelectedFinancialYear,
  selectFinancialYears,
  selectSelectedYearId,
} from "../../Redux/financialYearSlice.js";

const { Header, Content, Footer, Sider } = Layout;
const { Text } = Typography;

const AdminLayout = () => {
  const user = useSelector(getUserData);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // This context can still be used if other non-redux components rely on it
  const { setSelectedYear: setContextYear } = useSelectedYear();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [openKeys, setOpenKeys] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // ADDED: Get financial year data from the Redux store
  const financialYears = useSelector(selectFinancialYears);
  const selectedYearId = useSelector(selectSelectedYearId);

  // ADDED: Effect to fetch financial years on component mount
  useEffect(() => {
    dispatch(getCorporateFinancialYears())
      .then((data) => {
        // Store the full list in Redux
        dispatch(setFinancialYears(data));

        // Find and set the default year
        const defaultYear = data.find((year) => year.default_year === 1);
        if (defaultYear) {
          // Update both Redux and the Context for consistency
          dispatch(setSelectedFinancialYear(defaultYear.id));
          setContextYear(defaultYear.id);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch financial years:", error);
      });
  }, [dispatch, setContextYear]);


  useEffect(() => {
    fetchNotifications(); // initial fetch

    const interval = setInterval(() => {
      fetchNotifications();
    }, 1 * 60 * 1000); // every 5 minutes

    return () => clearInterval(interval);
  }, [dispatch, user?.corporate_id]);

  const fetchNotifications = async () => {
    try {
      const data = await dispatch(getNotificationData(user?.corporate_id));
      setNotifications(data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };
  const handleMenuClick = (e) => {
    setSelectedKeys([e.key]);
  };

  const handleLogout = () => {
    dispatch(setLoginData(null));
  };

  // MODIFIED: This handler now updates both Redux and the Context
  const handleDateSelect = (value) => {
    dispatch(setSelectedFinancialYear(value));
    setContextYear(value);
  };

  const handleProfileClick = () => {
    navigate("/corporate/MyAccount");
  };

  const dropItems = [
    {
      key: "profile",
      label: (
        <div className="d-flex align-items-center gap-2" onClick={handleProfileClick}>
          <UserOutlined /> Profile
        </div>
      ),
    },
    {
      key: "logout",
      label: (
        <div onClick={handleLogout} className="d-flex align-items-center gap-2">
          <LogoutOutlined style={{ fontSize: "15px" }} /> Logout
        </div>
      ),
    },
  ];

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    dispatch(updateNotificationData(user?.corporate_id))
      .then(() => {
        fetchNotifications();
      })
      .catch((error) => {
        console.error("Error marking notifications as read:", error);
      });
  };

  const items = [
    {
      key: "markAllRead",
      label: (
        <div style={{ textAlign: "center", padding: 8 }}>
          <a onClick={handleMarkAllRead} style={{ color: "#1890ff", fontWeight: 500 }}>
            {notifications.length !== 0 && "Mark all as read"}
          </a>
        </div>
      ),
      disabled: notifications.every(n => n.is_read === 1) || notifications.length === 0,
    },
    ...(notifications.length === 0
      ? [
        {
          key: "empty",
          label: (
            <div style={{ textAlign: "center", padding: 16, color: "#888" }}>
              <Empty
                description="No data available"
                image={Empty.PRESENTED_IMAGE_SIMPLE} >
              </Empty>
            </div>
          ),
          disabled: true,
        },
      ]
      : notifications.map((notification, idx) => ({
        key: idx,
        label: (
          <div style={{ padding: 8, background: notification.is_read === 1 ? "#f5f5f5" : "#e6f7ff" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ fontWeight: 600, color: "#1890ff" }}>{notification.template_key}</div>
              {notification.is_read === 0 && (
                <span style={{
                  background: "#1890ff",
                  color: "#fff",
                  fontSize: 10,
                  borderRadius: 4,
                  padding: "2px 6px",
                  marginLeft: 4,
                  fontWeight: 500
                }}>
                  NEW
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#333" }}>{notification.template}</div>
            <div style={{ fontSize: 12, color: "#888" }}>
              {notification.full_name} · {dayjs(notification.created_at).format("DD-MM-YYYY, HH:mm")}
            </div>
          </div>
        ),
        onClick: () => {
          setNotifications((prev) => {
            const updated = prev.map((n, i) =>
              i === idx ? { ...n, is_read: 1 } : n
            );
            if (updated.every(n => n.is_read === 1)) {
              dispatch(updateNotificationData(user?.corporate_id))
                .then(() => {
                  fetchNotifications();
                })
                .catch((error) => {
                  console.error("Error marking notifications as read:", error);
                });
            }
            return updated;
          });
        },
      }))
    ),
  ];

  return (
    <Layout className="cust-layout">
      <MenuHandler
        setSelectedKeys={setSelectedKeys}
        setOpenKeys={setOpenKeys}
      />
      <Sider
        className="custom-sider"
        breakpoint="lg"
        collapsedWidth="0"
      >
        <div className="logo mb-2">
          <img src={Logo} alt="logo" className="pannel-logo"></img>
          <div className="admin-head-sec">
            <div className="admin-info-column">
              <Avatar
                size={64}
                src={user?.corporate_logo?.logofile}
                icon={<UserOutlined />}
                className="admin-avatar"
              />
              <center> <h5 className="admin-text">{user?.corporate_name || "Guest"}</h5></center>
              <p>Corporate</p>

              <div className="adm-icons">
                <UserOutlined style={{ fontSize: "18px" }} onClick={handleProfileClick} />
                <PoweroffOutlined
                  style={{ fontSize: "18px", color: "red" }}
                  onClick={handleLogout}
                />
                <MailOutlined style={{ fontSize: "18px" }} />
              </div>
            </div>
          </div>
        </div>
        <Menu
          className="custom-menu"
          theme="dark"
          mode="inline"
          onClick={handleMenuClick}
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys)}
          items={MenuItems()}
        />
      </Sider>
      <Layout>
        <Header className={`site-layout-sub-header-background pannel-header`}>
          <div
            className="row justify-content-between align-items-center"
            style={{ width: "100%" }}
          >
            <div className="col col-xl-6 col-md-6 col-lg-6 col-sm-12 row align-items-center">
              <div className="col col-xl-3 col-md-3 col-lg-3 col-sm-6">
                <Select
                  value={selectedYearId}
                  className="com-head-select"
                  onSelect={handleDateSelect}
                  options={financialYears.map(year => ({
                    value: year.id,
                    label: year.financial_year,
                  }))}
                  placeholder="Loading..."
                />
              </div>
              <div className="col-xl-5 col-md-5 col-lg-5 col-sm-6">
                {" "}
                <Translate />
              </div>
            </div>

            <div className="col col-xl-3 col-md-3 col-lg-3 col-sm-12">
              <Space direction="horizontal" size="large" className="d-flex justify-content-end align-items-center">
                <Dropdown
                  menu={{ items }}
                  trigger={["click"]}
                  dropdownRender={(menu) => (
                    <div
                      style={{
                        minHeight: 200,
                        maxHeight: 320,
                        overflowY: "auto",
                        width: 340,
                        padding: 0,
                        background: "#fff",
                        borderRadius: 8,
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      }}
                    >
                      {menu}
                    </div>
                  )}
                >
                  <a onClick={(e) => e.preventDefault()}>
                    <Space>
                      <Badge
                        count={
                          notifications.filter((n) => n.is_read === 0).length
                        }
                        overflowCount={9}
                      >
                        <BellOutlined
                          style={{
                            fontSize: 22,
                            color: "#fff",
                            cursor: "pointer",
                          }}
                        />
                      </Badge>
                    </Space>
                  </a>
                </Dropdown>
                <Dropdown
                  menu={{
                    items: dropItems,
                  }}
                  trigger={["click"]}
                  className="pannel-drop"
                >
                  <div
                    onClick={(e) => e.preventDefault()}
                    style={{ cursor: "pointer" }}
                  >
                    <Avatar src={user?.corporate_logo?.logofile} icon={<UserOutlined />} className="d-flex" />
                    <Text style={{ marginLeft: 8, color: "#fff" }}>
                      <center>{user?.fullname || "Guest"}</center>
                    </Text>
                    <DownOutlined style={{ marginLeft: 4, color: "#fff" }} />
                  </div>
                </Dropdown>
              </Space>
            </div>
          </div>
        </Header>
        <Content className="custom-content">
          <div
            className="site-layout-background"
            style={{
              padding: 10,
              minHeight: "100%",
            }}
          >
            <h3 className="layout-heading mb-4">Hi, Welcome back!</h3>
            <Outlet context={{ selectedYearId }} /> {/* Pass ID from Redux */}
          </div>
        </Content>
        <Footer
          style={{
            textAlign: "center",
            padding: "14px 50px",
          }}
        >
          Copyright © {new Date().getFullYear()} VIBE V3.4.0
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;